import type { Permission } from "./index";

export enum TenantAuthorizationContext {
  MonoBranch = "MONO_BRANCH",
  MultiBranchParish = "MULTI_BRANCH_PARISH",
  MultiBranchBranch = "MULTI_BRANCH_BRANCH"
}

export enum TenantAuthorizationAction {
  DashboardView = "DASHBOARD_VIEW",
  MembersView = "MEMBERS_VIEW",
  MembersEdit = "MEMBERS_EDIT",
  FinanceView = "FINANCE_VIEW",
  TransactionCreate = "TRANSACTION_CREATE",
  TransactionCancel = "TRANSACTION_CANCEL",
  ManualJournalCreate = "MANUAL_JOURNAL_CREATE",
  TransferApprove = "TRANSFER_APPROVE",
  ReconciliationApprove = "RECONCILIATION_APPROVE",
  FiscalPeriodClose = "FISCAL_PERIOD_CLOSE",
  BudgetingView = "BUDGETING_VIEW",
  BudgetingEdit = "BUDGETING_EDIT",
  BudgetApprove = "BUDGET_APPROVE",
  BudgetCreate = "BUDGET_CREATE",
  BudgetSubmit = "BUDGET_SUBMIT",
  BudgetReview = "BUDGET_REVIEW",
  BudgetRequestChanges = "BUDGET_REQUEST_CHANGES",
  BudgetMarkReady = "BUDGET_MARK_READY",
  BudgetArchive = "BUDGET_ARCHIVE",
  BudgetRevise = "BUDGET_REVISE",
  BudgetClose = "BUDGET_CLOSE",
  ActivitiesView = "ACTIVITIES_VIEW",
  ActivitiesEdit = "ACTIVITIES_EDIT",
  ActivityBudgetView = "ACTIVITY_BUDGET_VIEW",
  ActivityBudgetCreate = "ACTIVITY_BUDGET_CREATE",
  ActivityBudgetEdit = "ACTIVITY_BUDGET_EDIT",
  ActivityBudgetSubmit = "ACTIVITY_BUDGET_SUBMIT",
  ActivityPledgeView = "ACTIVITY_PLEDGE_VIEW",
  ActivityPledgeCreate = "ACTIVITY_PLEDGE_CREATE",
  ActivityPledgeEdit = "ACTIVITY_PLEDGE_EDIT",
  ActivityPledgeFulfill = "ACTIVITY_PLEDGE_FULFILL",
  ActivityTransactionView = "ACTIVITY_TRANSACTION_VIEW",
  ActivityTransactionCreate = "ACTIVITY_TRANSACTION_CREATE",
  ActivityReportView = "ACTIVITY_REPORT_VIEW",
  ReportsView = "REPORTS_VIEW",
  ReportsEdit = "REPORTS_EDIT",
  AssetsView = "ASSETS_VIEW",
  AssetsEdit = "ASSETS_EDIT",
  GroupsView = "GROUPS_VIEW",
  GroupsEdit = "GROUPS_EDIT",
  GroupMembershipManage = "GROUP_MEMBERSHIP_MANAGE",
  MinistriesView = "MINISTRIES_VIEW",
  MinistriesEdit = "MINISTRIES_EDIT",
  MinistryMembershipManage = "MINISTRY_MEMBERSHIP_MANAGE",
  FormsView = "FORMS_VIEW",
  FormsEdit = "FORMS_EDIT",
  FormPublish = "FORM_PUBLISH",
  FormSubmissionsView = "FORM_SUBMISSIONS_VIEW",
  FormSubmissionProcess = "FORM_SUBMISSION_PROCESS",
  DepartmentsView = "DEPARTMENTS_VIEW",
  DepartmentsEdit = "DEPARTMENTS_EDIT",
  SettingsView = "SETTINGS_VIEW",
  SettingsEdit = "SETTINGS_EDIT",
  WorkspaceSettingsView = "WORKSPACE_SETTINGS_VIEW",
  WorkspaceSettingsEdit = "WORKSPACE_SETTINGS_EDIT",
  AuditLogView = "AUDIT_LOG_VIEW",
  BranchesView = "BRANCHES_VIEW",
  BranchesEdit = "BRANCHES_EDIT",
  BranchesManage = "BRANCHES_MANAGE",
  BranchInspect = "BRANCH_INSPECT",
  RolesView = "ROLES_VIEW",
  RolesEdit = "ROLES_EDIT",
  RolesManage = "ROLES_MANAGE",
  SubscriptionView = "SUBSCRIPTION_VIEW",
  SubscriptionEdit = "SUBSCRIPTION_EDIT",
  SubscriptionManage = "SUBSCRIPTION_MANAGE",
  UsersView = "USERS_VIEW",
  UsersEdit = "USERS_EDIT",
  BranchTeamView = "BRANCH_TEAM_VIEW",
  BranchTeamManage = "BRANCH_TEAM_MANAGE"
}

export enum TenantAuthorizationDecisionReason {
  Allowed = "ALLOWED",
  MissingAuthentication = "MISSING_AUTHENTICATION",
  WorkspaceAccessRequired = "WORKSPACE_ACCESS_REQUIRED",
  ContextDoesNotMatchWorkspace = "CONTEXT_DOES_NOT_MATCH_WORKSPACE",
  BranchRequired = "BRANCH_REQUIRED",
  BranchNotFound = "BRANCH_NOT_FOUND",
  BranchInactive = "BRANCH_INACTIVE",
  BranchAssignmentRequired = "BRANCH_ASSIGNMENT_REQUIRED",
  ParishContextRequired = "PARISH_CONTEXT_REQUIRED",
  ResourceRequired = "RESOURCE_REQUIRED",
  ResourceNotFound = "RESOURCE_NOT_FOUND",
  ResourceScopeMismatch = "RESOURCE_SCOPE_MISMATCH",
  PermissionMissing = "PERMISSION_MISSING",
  EntitlementMissing = "ENTITLEMENT_MISSING",
  ActionForbidden = "ACTION_FORBIDDEN"
}

export enum TenantAuthorizationSource {
  Owner = "OWNER",
  Direct = "DIRECT",
  Inherited = "INHERITED"
}

export enum TenantAuthorizationResourceType {
  Activity = "ACTIVITY",
  ActivityBudget = "ACTIVITY_BUDGET",
  ActivityPledge = "ACTIVITY_PLEDGE",
  ActivityTransaction = "ACTIVITY_TRANSACTION",
  Department = "DEPARTMENT",
  Group = "GROUP",
  Ministry = "MINISTRY",
  Form = "FORM",
  BranchUserChange = "BRANCH_USER_CHANGE"
}

export type TenantAuthorizationResource =
  | { type: TenantAuthorizationResourceType.Activity; activityId: string }
  | { type: TenantAuthorizationResourceType.ActivityBudget; activityId: string; budgetId?: string }
  | { type: TenantAuthorizationResourceType.ActivityPledge; activityId: string; pledgeId?: string }
  | { type: TenantAuthorizationResourceType.ActivityTransaction; activityId: string; transactionId?: string }
  | { type: TenantAuthorizationResourceType.Department; departmentId: string }
  | { type: TenantAuthorizationResourceType.Group; groupId: string }
  | { type: TenantAuthorizationResourceType.Ministry; ministryId: string }
  | { type: TenantAuthorizationResourceType.Form; formId: string }
  | {
      type: TenantAuthorizationResourceType.BranchUserChange;
      targetBranchId: string;
      targetWorkspaceUserId?: string;
      requestedWorkspaceRole: "OWNER" | "ADMIN" | "USER";
      requestedBranchRole: "ADMIN" | "USER";
      requestedRoleIds: string[];
    };

export type TenantAuthorizationConstraints = {
  branchId?: string;
  activityId?: string;
  financeActivityId?: string;
  departmentId?: string;
  groupId?: string;
  ministryId?: string;
  formId?: string;
  targetBranchId?: string;
};

export enum TenantAuthorizationInheritanceRule {
  ActivityPrepareFinance = "ACTIVITY_PREPARE_FINANCE",
  ActivityOwnedOperations = "ACTIVITY_OWNED_OPERATIONS",
  BranchTeamAdministration = "BRANCH_TEAM_ADMINISTRATION",
  BranchOperationalRead = "BRANCH_OPERATIONAL_READ",
  DepartmentOwnedOperations = "DEPARTMENT_OWNED_OPERATIONS",
  GroupMembershipAdministration = "GROUP_MEMBERSHIP_ADMINISTRATION",
  MinistryMembershipAdministration = "MINISTRY_MEMBERSHIP_ADMINISTRATION",
  FormOwnedOperations = "FORM_OWNED_OPERATIONS"
}

export type TenantAuthorizationActionDefinition = {
  allowedContexts: readonly TenantAuthorizationContext[];
  directPermissions: readonly Permission[];
  requiredResource?: TenantAuthorizationResourceType;
  inheritedBy?: readonly TenantAuthorizationInheritanceRule[];
  disclosurePolicy?: "FINANCE";
};

const BRANCH = [TenantAuthorizationContext.MonoBranch, TenantAuthorizationContext.MultiBranchBranch] as const;
const ALL_CONTEXTS = [...BRANCH, TenantAuthorizationContext.MultiBranchParish] as const;
const WORKSPACE = [TenantAuthorizationContext.MonoBranch, TenantAuthorizationContext.MultiBranchParish] as const;

const definition = (
  allowedContexts: readonly TenantAuthorizationContext[],
  directPermissions: readonly Permission[],
  extra: Omit<TenantAuthorizationActionDefinition, "allowedContexts" | "directPermissions"> = {}
): TenantAuthorizationActionDefinition => ({ allowedContexts, directPermissions, ...extra });

const view = (module: string): Permission[] => [`${module}.view`, `${module}.edit`, `${module}.manage`] as Permission[];
const edit = (module: string): Permission[] => [`${module}.edit`, `${module}.manage`] as Permission[];
const approve = (module: string): Permission[] => [`${module}.approve`, `${module}.manage`] as Permission[];
const readManage = (module: string): Permission[] => [`${module}.view`, `${module}.manage`] as Permission[];

export const TENANT_AUTHORIZATION_ACTION_DEFINITIONS: Readonly<Record<TenantAuthorizationAction, TenantAuthorizationActionDefinition>> = {
  [TenantAuthorizationAction.DashboardView]: definition(ALL_CONTEXTS, []),
  [TenantAuthorizationAction.MembersView]: definition(ALL_CONTEXTS, view("members"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchOperationalRead] }),
  [TenantAuthorizationAction.MembersEdit]: definition(BRANCH, edit("members")),
  [TenantAuthorizationAction.FinanceView]: definition(ALL_CONTEXTS, view("finance"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchOperationalRead], disclosurePolicy: "FINANCE" }),
  [TenantAuthorizationAction.TransactionCreate]: definition(ALL_CONTEXTS, edit("finance")),
  [TenantAuthorizationAction.TransactionCancel]: definition(ALL_CONTEXTS, approve("finance")),
  [TenantAuthorizationAction.ManualJournalCreate]: definition(ALL_CONTEXTS, ["finance.manage"]),
  [TenantAuthorizationAction.TransferApprove]: definition(ALL_CONTEXTS, ["finance.edit", "finance.approve", "finance.manage"]),
  [TenantAuthorizationAction.ReconciliationApprove]: definition(ALL_CONTEXTS, approve("finance")),
  [TenantAuthorizationAction.FiscalPeriodClose]: definition(ALL_CONTEXTS, edit("finance")),
  [TenantAuthorizationAction.BudgetingView]: definition(ALL_CONTEXTS, view("budgets")),
  [TenantAuthorizationAction.BudgetingEdit]: definition(ALL_CONTEXTS, edit("budgets")),
  [TenantAuthorizationAction.BudgetApprove]: definition(ALL_CONTEXTS, approve("budgets")),
  [TenantAuthorizationAction.BudgetCreate]: definition(ALL_CONTEXTS, edit("budgets")),
  [TenantAuthorizationAction.BudgetSubmit]: definition(ALL_CONTEXTS, edit("budgets")),
  [TenantAuthorizationAction.BudgetReview]: definition(ALL_CONTEXTS, approve("budgets")),
  [TenantAuthorizationAction.BudgetRequestChanges]: definition(ALL_CONTEXTS, approve("budgets")),
  [TenantAuthorizationAction.BudgetMarkReady]: definition(ALL_CONTEXTS, approve("budgets")),
  [TenantAuthorizationAction.BudgetArchive]: definition(ALL_CONTEXTS, edit("budgets")),
  [TenantAuthorizationAction.BudgetRevise]: definition(ALL_CONTEXTS, ["budgets.manage"]),
  [TenantAuthorizationAction.BudgetClose]: definition(ALL_CONTEXTS, ["budgets.manage"]),
  [TenantAuthorizationAction.ActivitiesView]: definition(BRANCH, view("activities")),
  [TenantAuthorizationAction.ActivitiesEdit]: definition(BRANCH, edit("activities")),
  [TenantAuthorizationAction.ActivityBudgetView]: definition(BRANCH, view("budgets"), { requiredResource: TenantAuthorizationResourceType.ActivityBudget, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance], disclosurePolicy: "FINANCE" }),
  [TenantAuthorizationAction.ActivityBudgetCreate]: definition(BRANCH, edit("budgets"), { requiredResource: TenantAuthorizationResourceType.ActivityBudget, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityBudgetEdit]: definition(BRANCH, edit("budgets"), { requiredResource: TenantAuthorizationResourceType.ActivityBudget, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityBudgetSubmit]: definition(BRANCH, edit("budgets"), { requiredResource: TenantAuthorizationResourceType.ActivityBudget, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityPledgeView]: definition(BRANCH, view("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityPledge, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance], disclosurePolicy: "FINANCE" }),
  [TenantAuthorizationAction.ActivityPledgeCreate]: definition(BRANCH, edit("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityPledge, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityPledgeEdit]: definition(BRANCH, edit("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityPledge, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityPledgeFulfill]: definition(BRANCH, edit("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityPledge, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityTransactionView]: definition(BRANCH, view("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityTransaction, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance], disclosurePolicy: "FINANCE" }),
  [TenantAuthorizationAction.ActivityTransactionCreate]: definition(BRANCH, edit("finance"), { requiredResource: TenantAuthorizationResourceType.ActivityTransaction, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance] }),
  [TenantAuthorizationAction.ActivityReportView]: definition(BRANCH, readManage("reports"), { requiredResource: TenantAuthorizationResourceType.Activity, inheritedBy: [TenantAuthorizationInheritanceRule.ActivityPrepareFinance], disclosurePolicy: "FINANCE" }),
  [TenantAuthorizationAction.ReportsView]: definition(BRANCH, readManage("reports")),
  [TenantAuthorizationAction.ReportsEdit]: definition(BRANCH, ["reports.manage"]),
  [TenantAuthorizationAction.AssetsView]: definition(BRANCH, view("assets")),
  [TenantAuthorizationAction.AssetsEdit]: definition(BRANCH, edit("assets")),
  [TenantAuthorizationAction.GroupsView]: definition(BRANCH, view("groups")),
  [TenantAuthorizationAction.GroupsEdit]: definition(BRANCH, edit("groups")),
  [TenantAuthorizationAction.GroupMembershipManage]: definition(BRANCH, edit("groups"), { requiredResource: TenantAuthorizationResourceType.Group, inheritedBy: [TenantAuthorizationInheritanceRule.GroupMembershipAdministration] }),
  [TenantAuthorizationAction.MinistriesView]: definition(ALL_CONTEXTS, view("ministries"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchOperationalRead] }),
  [TenantAuthorizationAction.MinistriesEdit]: definition(ALL_CONTEXTS, edit("ministries")),
  [TenantAuthorizationAction.MinistryMembershipManage]: definition(BRANCH, edit("ministries"), { requiredResource: TenantAuthorizationResourceType.Ministry, inheritedBy: [TenantAuthorizationInheritanceRule.MinistryMembershipAdministration] }),
  [TenantAuthorizationAction.FormsView]: definition(BRANCH, view("forms")),
  [TenantAuthorizationAction.FormsEdit]: definition(BRANCH, edit("forms")),
  [TenantAuthorizationAction.FormPublish]: definition(BRANCH, ["forms.manage"], { requiredResource: TenantAuthorizationResourceType.Form, inheritedBy: [TenantAuthorizationInheritanceRule.FormOwnedOperations] }),
  [TenantAuthorizationAction.FormSubmissionsView]: definition(BRANCH, view("forms"), { requiredResource: TenantAuthorizationResourceType.Form, inheritedBy: [TenantAuthorizationInheritanceRule.FormOwnedOperations] }),
  [TenantAuthorizationAction.FormSubmissionProcess]: definition(BRANCH, edit("forms"), { requiredResource: TenantAuthorizationResourceType.Form, inheritedBy: [TenantAuthorizationInheritanceRule.FormOwnedOperations] }),
  [TenantAuthorizationAction.DepartmentsView]: definition(BRANCH, view("departments"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchOperationalRead] }),
  [TenantAuthorizationAction.DepartmentsEdit]: definition(BRANCH, edit("departments")),
  [TenantAuthorizationAction.SettingsView]: definition(WORKSPACE, readManage("settings")),
  [TenantAuthorizationAction.SettingsEdit]: definition(WORKSPACE, ["settings.manage"]),
  [TenantAuthorizationAction.WorkspaceSettingsView]: definition([TenantAuthorizationContext.MonoBranch], readManage("workspace_settings")),
  [TenantAuthorizationAction.WorkspaceSettingsEdit]: definition([TenantAuthorizationContext.MonoBranch], ["workspace_settings.manage"]),
  [TenantAuthorizationAction.AuditLogView]: definition(WORKSPACE, readManage("settings")),
  [TenantAuthorizationAction.BranchesView]: definition(WORKSPACE, view("branches")),
  [TenantAuthorizationAction.BranchesEdit]: definition(WORKSPACE, edit("branches")),
  [TenantAuthorizationAction.BranchesManage]: definition(WORKSPACE, ["branches.manage"]),
  [TenantAuthorizationAction.BranchInspect]: definition(BRANCH, view("branches"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchOperationalRead] }),
  [TenantAuthorizationAction.RolesView]: definition(WORKSPACE, readManage("roles")),
  [TenantAuthorizationAction.RolesEdit]: definition(WORKSPACE, ["roles.manage"]),
  [TenantAuthorizationAction.RolesManage]: definition(WORKSPACE, ["roles.manage"]),
  [TenantAuthorizationAction.SubscriptionView]: definition(WORKSPACE, readManage("subscription")),
  [TenantAuthorizationAction.SubscriptionEdit]: definition(WORKSPACE, ["subscription.manage"]),
  [TenantAuthorizationAction.SubscriptionManage]: definition(WORKSPACE, ["subscription.manage"]),
  [TenantAuthorizationAction.UsersView]: definition(ALL_CONTEXTS, view("users"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchTeamAdministration] }),
  [TenantAuthorizationAction.UsersEdit]: definition(ALL_CONTEXTS, edit("users"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchTeamAdministration] }),
  [TenantAuthorizationAction.BranchTeamView]: definition(BRANCH, view("users"), { inheritedBy: [TenantAuthorizationInheritanceRule.BranchTeamAdministration] }),
  [TenantAuthorizationAction.BranchTeamManage]: definition(BRANCH, edit("users"), { requiredResource: TenantAuthorizationResourceType.BranchUserChange, inheritedBy: [TenantAuthorizationInheritanceRule.BranchTeamAdministration] })
};

export function tenantAuthorizationActionDefinition(action: TenantAuthorizationAction): TenantAuthorizationActionDefinition | undefined {
  return TENANT_AUTHORIZATION_ACTION_DEFINITIONS[action];
}
